import { Box, Button, Flex, SelectMenu, SvgIcon } from '@volue/wave-react';
import React from 'react';

import type {
  CalculationState,
  CompletedCalculationTactic
} from '@/features/execute-calculation-set/overmind/calculation';
import type { TacticResultBrowsingFilter } from '@/features/execute-calculation-set/overmind/calculation/actions';
import { useActions, useAppState } from '@/overmind';

export function LoadFlowScenarioSelector() {
  const scenariosRequiringAttention =
    useAppState().calculation.loadFlowScenariosRequiringAttention;
  const [selectedScenario, setSelectedScenario] =
    React.useState<string>('All scenarios');
  const browseTacticResults = useActions().calculation.browseTacticResults;

  const calculationResult =
    useAppState().calculation.calculationTactic.matches('LOADED')?.value;

  const completedCalculationTactic =
    calculationResult as CompletedCalculationTactic;

  const scenarios =
    completedCalculationTactic.calculationSetResult.calculationScenarios.loadFlow.map(
      scenario => ({
        scenarioName: scenario.scenarioName,
        scenarioId: scenario.scenarioId
      })
    );

  const tacticResultBrowsingFilter =
    useAppState().calculation.tacticResultBrowsingFilter;
  const previousTacticResultBrowsingFilter =
    useAppState().calculation.previousTacticResultBrowsingFilter;
  const selectorLabel = getScenarioSelectorLabel(
    tacticResultBrowsingFilter,
    previousTacticResultBrowsingFilter,
    scenarios,
    selectedScenario
  );

  React.useEffect(() => {
    const selectorLabel = getScenarioSelectorLabel(
      tacticResultBrowsingFilter,
      previousTacticResultBrowsingFilter,
      scenarios,
      selectedScenario
    );
    setSelectedScenario(selectorLabel);
  }, [tacticResultBrowsingFilter]);

  const AttentionIcon = (props: { isVisible: boolean }) =>
    props.isVisible ? (
      <SvgIcon iconName="warning" css={{ color: '$colorOrange80' }} />
    ) : (
      <></>
    );
  const currentScenario = scenarios.find(
    scenario => scenario.scenarioName === selectedScenario
  );
  return (
    <span
      data-component={LoadFlowScenarioSelector.name}
      style={{ display: 'contents' }}
    >
      <SelectMenu
        value={selectorLabel}
        onValueChange={value => setSelectedScenario(value)}
      >
        <Button
          variant="ghost"
          onClick={() => {
            browseTacticResults({
              type: 'SELECTED_LOAD_FLOW',
              scenarioIds: currentScenario
                ? [currentScenario.scenarioId]
                : scenarios.map(scenario => scenario.scenarioId)
            });
          }}
        >
          <Flex alignItems={'center'} justifyContent={'space-around'}>
            {selectorLabel === 'All scenarios' ? (
              anyScenariosRequireAttention(scenariosRequiringAttention) ? (
                <AttentionIcon isVisible />
              ) : (
                <SvgIcon
                  iconName="validation"
                  css={{ color: '$colorGreen70' }}
                />
              )
            ) : shouldIndicateRequiredAttention(
                currentScenario,
                scenariosRequiringAttention
              ) ? (
              <AttentionIcon isVisible />
            ) : (
              <SvgIcon iconName="validation" css={{ color: '$colorGreen70' }} />
            )}
            <span>{shamefullyFormatScenarioName(selectorLabel)}</span>
          </Flex>
        </Button>
        <SelectMenu.Trigger
          aria-label="Loadflow scenarios"
          css={{ paddingLeft: '$spacingS', paddingRight: '$spacingS' }}
          as={SelectMenu.Icon}
        >
          <SelectMenu.Value placeholder="All scenarios" />
        </SelectMenu.Trigger>

        <SelectMenu.Content>
          <React.Fragment key="">
            <SelectMenu.Group>
              <SelectMenu.Item
                startElement={
                  anyScenariosRequireAttention(scenariosRequiringAttention) ? (
                    <AttentionIcon isVisible />
                  ) : (
                    <SvgIcon
                      iconName="validation"
                      css={{ color: '$colorGreen70' }}
                    />
                  )
                }
                key="All scenarios"
                onClick={() =>
                  browseTacticResults({
                    type: 'SELECTED_LOAD_FLOW',
                    scenarioIds: scenarios.map(scenario => scenario.scenarioId)
                  })
                }
                value="All scenarios"
              >
                <Box css={{ textAlign: 'center' }}>All scenarios</Box>
              </SelectMenu.Item>
            </SelectMenu.Group>
            <SelectMenu.Separator />

            <SelectMenu.Group>
              {scenarios.map(scenario => (
                <SelectMenu.Item
                  startElement={
                    !shouldIndicateRequiredAttention(
                      scenario,
                      scenariosRequiringAttention
                    ) ? (
                      <SvgIcon
                        iconName="validation"
                        css={{ color: '$colorGreen70' }}
                      />
                    ) : (
                      <SvgIcon
                        iconName="warning"
                        css={{ color: '$colorOrange80' }}
                      />
                    )
                  }
                  key={scenario.scenarioName}
                  onClick={() =>
                    browseTacticResults({
                      type: 'SELECTED_LOAD_FLOW',
                      scenarioIds: [scenario.scenarioId]
                    })
                  }
                  value={scenario.scenarioName}
                >
                  {scenario.scenarioName}
                </SelectMenu.Item>
              ))}
            </SelectMenu.Group>
          </React.Fragment>
        </SelectMenu.Content>
      </SelectMenu>
    </span>
  );
}

function shouldIndicateRequiredAttention(
  currentScenario: { scenarioName: string; scenarioId: string } | undefined,
  scenariosRequiringAttention: CalculationState['loadFlowScenariosRequiringAttention']
) {
  if (scenariosRequiringAttention.type === 'NOT_AVAILABLE') return false;
  return (
    currentScenario &&
    scenariosRequiringAttention.scenarios[currentScenario.scenarioId]
  );
}

function getScenarioSelectorLabel(
  tacticResultBrowsingFilter: TacticResultBrowsingFilter,
  previousTacticResultBrowsingFilter: TacticResultBrowsingFilter,
  scenarios: { scenarioName: string; scenarioId: string }[],
  selectedScenario: string
) {
  if (
    tacticResultBrowsingFilter.type === 'SHORT_CIRCUIT' &&
    previousTacticResultBrowsingFilter.type === 'SELECTED_LOAD_FLOW' &&
    previousTacticResultBrowsingFilter.scenarioIds.length === 1
  ) {
    return (
      scenarios.find(
        scenario =>
          scenario.scenarioId ===
          previousTacticResultBrowsingFilter.scenarioIds[0]
      )?.scenarioName || selectedScenario
    );
  }

  return (
    (tacticResultBrowsingFilter.type === 'SELECTED_LOAD_FLOW' &&
      (tacticResultBrowsingFilter.scenarioIds.length === scenarios.length
        ? 'All scenarios'
        : scenarios.find(
            scenario =>
              scenario.scenarioId === tacticResultBrowsingFilter.scenarioIds[0]
          )?.scenarioName)) ||
    'All scenarios'
  );
}
function anyScenariosRequireAttention(
  scenariosRequiringAttention: CalculationState['loadFlowScenariosRequiringAttention']
) {
  if (scenariosRequiringAttention.type === 'NOT_AVAILABLE') return false;
  return Object.values(scenariosRequiringAttention.scenarios).some(
    scenario => scenario
  );
}

const shamefulScenarioNamePrefix = 'Load flow';

function shamefullyFindShortScenarioName(selectorLabel: string): string {
  if (selectorLabel === 'All scenarios') {
    return 'All';
  } else if (selectorLabel.toLowerCase().includes('high')) {
    return 'High';
  } else if (selectorLabel.toLowerCase().includes('low')) {
    return 'Low';
  } else if (selectorLabel.toLowerCase().includes('normal')) {
    return 'Normal';
  }

  return selectorLabel;
}
function shamefullyFormatScenarioName(selectorLabel: string): React.ReactNode {
  return `${shamefulScenarioNamePrefix} - ${shamefullyFindShortScenarioName(
    selectorLabel
  )}`;
}
